import React, { useState, useEffect } from "react";

import { Formik, Form, Field } from "formik";
import { isEmpty } from "ramda";

import dataSourcesApi from "apis/dataSource";
import dsdeApi from "apis/dsde";
import { Typography, Button } from "clearsense-ui/components";
import { Select, Input, Checkbox } from "clearsense-ui/components/formik";
import ContentBox from "components/Common/ContentBox";

import {
  DSDE_MAPPING_INITIAL_VALUES,
  DSDE_MAPPING_VALIDATION_SCHEMA,
} from "../constants/DSDE";

const DataSourceDataElementMapping = ({ dataSource, cde, dsde, fetchDsde }) => {
  const [tableNames, setTableNames] = useState([]);
  const [isSelectLoading, setIsSelectLoading] = useState(false);

  const fetchTablesAndColumns = async () => {
    if (isEmpty(dataSource)) return;

    try {
      setIsSelectLoading(true);
      const { data } = await dataSourcesApi.getTablesAndColumns(
        dataSource.db_name
      );
      setTableNames(data.tables_with_columns);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsSelectLoading(false);
    }
  };

  const onSubmit = async values => {
    try {
      const payload = {
        table_name: values?.tableName?.value,
        data_source_id: dataSource?.id,
        cde_definition_id: cde?.id,
        column_name: values?.columnName?.value,
        data_type: values.datatype,
        column_length: values.columnLength,
        pattern: values.pattern,
        unique_flag: values.uniqueFlag,
        is_secured: values.isSecure ? values.phi : "",
      };
      dsde?.id ? await dsdeApi.update(payload) : await dsdeApi.create(payload);
      fetchDsde();
    } catch (error) {
      logger.log(error);
    }
  };

  const findColumnsOfTable = table => {
    const tableIndex = tableNames.findIndex(
      tableName => tableName.table_name === table
    );
    return tableNames[tableIndex]?.columns?.map(column => ({
      label: column,
      value: column,
    }));
  };

  useEffect(() => {
    fetchTablesAndColumns();
  }, [dataSource]);

  useEffect(() => {
    fetchDsde();
  }, [cde, dataSource]);

  return (
    <div className="flex flex-col gap-5 my-4 ml-6">
      <ContentBox className="w-1/2">
        <Formik
          initialValues={
            dsde?.id
              ? {
                  tableName: {
                    label: dsde?.table_name,
                    value: dsde?.table_name,
                  },
                  columnName: {
                    label: dsde?.column_name,
                    value: dsde?.column_name,
                  },
                  datatype: dsde?.data_type,
                  columnLength: dsde?.column_length,
                  pattern: dsde?.pattern,
                  uniqueFlag: dsde?.unique_flag,
                  phi: dsde?.is_secured,
                  isSecure: !!dsde?.is_secured,
                }
              : DSDE_MAPPING_INITIAL_VALUES
          }
          validationSchema={DSDE_MAPPING_VALIDATION_SCHEMA}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <Form className="flex flex-col gap-4">
              <Typography style="h4" type="semibold">
                Data Source Data Element
              </Typography>
              <Input
                size="small"
                name="dataSource"
                label="Data Source"
                disabled
                value={dataSource?.name ?? ""}
              />
              <Select
                size="small"
                name="tableName"
                label="Table Name"
                placeholder="Select Table Name"
                isLoading={isSelectLoading}
                options={
                  tableNames?.length > 0 &&
                  tableNames?.map(tableName => ({
                    label: tableName.table_name,
                    value: tableName.table_name,
                  }))
                }
              />
              <Select
                size="small"
                name="columnName"
                label="Column Name"
                placeholder="Select Column Name"
                isDisabled={isSelectLoading || !values?.tableName?.label}
                options={
                  values?.tableName?.label &&
                  findColumnsOfTable(values?.tableName?.label)
                }
              />
              <Input
                size="small"
                name="datatype"
                label="Data Type"
                placeholder="Enter Data Type"
              />
              <Input
                size="small"
                name="columnLength"
                label="Column Length"
                placeholder="Enter Column Length"
              />
              <Input
                size="small"
                name="pattern"
                label="Pattern"
                placeholder="Enter Pattern here"
              />
              <Checkbox label="Unique Flag" name="uniqueFlag" />
              <Checkbox label="Is Secure" name="isSecure" />
              {values.isSecure && (
                <div className="flex gap-4">
                  <label className="flex items-center gap-4">
                    <Field type="radio" name="phi" value="phi" label="PHI" />
                    PHI
                  </label>
                  <label className="flex items-center gap-4">
                    <Field
                      type="radio"
                      name="phi"
                      value="businessSensitive"
                      label="Business Sensitive"
                    />
                    Business Sensitive
                  </label>
                </div>
              )}
              <div className="flex justify-start">
                <Button style="primary" type="submit" label="Save Changes" />
                <Button style="text" type="reset" label="Cancel" />
              </div>
            </Form>
          )}
        </Formik>
      </ContentBox>
    </div>
  );
};

export default DataSourceDataElementMapping;
